import React from "react"
import { FormControl, Grid, InputLabel, Link, MenuItem, Select, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { LocalizedEnum, LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { StandardDateRangeFilter } from "./StandardDateRangeFilter"
import { DatePeriod } from "../filters/selectDatePeriodFilter/SelectDatePeriodFilter"
import { DateTime } from "luxon"
import { TranslationKey } from "../../../modules/localization/translations"

const useStyles = makeStyles()((theme) => ({
    dateFilter: {
        maxWidth: "11rem",
        minWidth: "10rem",
    },
    container: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    changeTimeButtonSelect: {
        cursor: "pointer",
        whiteSpace: "nowrap",
    },
    changeTimeButtonDisabled: {
        color: theme.palette.text.disabled,
        pointerEvents: "none",
    },
    inputClasses: {
        padding: "0.5rem 1rem",
    },
    selectClasses: {
        ".MuiSelect-select": {
            padding: "0.5rem 1rem",
        },
    },
    dateRangeFilter: {
        width: "16.5rem",
    },
}))

type DateState = { startDate: DateTime; endDate: DateTime }

type TimeOrDatePickerProps<T extends string = DatePeriod> = Readonly<{
    isDatePicker: boolean
    excludeDateTime?: boolean
    disabled?: boolean
    dateState: DateState
    setDateState: (value: DateState) => void
    timePeriod: T
    handleChangeTimePeriod: (value: T) => void
    handleChangeTimeOrDatePicker: () => void
    timePeriodOptions?: readonly T[] | T[]
    translationBase?: string
    timeLabel?: string
    timeLabelTranslationKey?: TranslationKey
}>

/**
 * Unified Time/Date range picker component.
 * Switches between a date range picker and a time period dropdown,
 * with a toggle button to switch modes.
 *
 * @example
 * // With default DatePeriod (no options needed)
 * const [isDatePicker, setIsDatePicker] = useState(false)
 * const [dateState, setDateState] = useState(null)
 * const [datePeriod, setDatePeriod] = useState(DatePeriod.TODAY)
 *
 * <TimeOrDatePicker
 *     isDatePicker={isDatePicker}
 *     dateState={dateState}
 *     setDateState={setDateState}
 *     timePeriod={datePeriod}
 *     handleChangeTimePeriod={setDatePeriod}
 *     handleChangeTimeOrDatePicker={() => setIsDatePicker(!isDatePicker)}
 * />
 *
 * @example
 * // With custom enum type
 * enum CustomPeriod {
 *     DAILY = "DAILY",
 *     WEEKLY = "WEEKLY",
 *     MONTHLY = "MONTHLY"
 * }
 * const [timePeriod, setTimePeriod] = useState(CustomPeriod.DAILY)
 *
 * <TimeOrDatePicker<CustomPeriod>
 *     isDatePicker={isDatePicker}
 *     dateState={dateState}
 *     setDateState={setDateState}
 *     timePeriod={timePeriod}
 *     handleChangeTimePeriod={setTimePeriod}
 *     handleChangeTimeOrDatePicker={() => setIsDatePicker(!isDatePicker)}
 *     timePeriodOptions={Object.values(CustomPeriod)}
 * />
 */

export const TimeOrDatePicker = <T extends string = DatePeriod>({
    isDatePicker,
    excludeDateTime,
    disabled = false,
    dateState,
    setDateState,
    timePeriod,
    handleChangeTimePeriod,
    handleChangeTimeOrDatePicker,
    timePeriodOptions,
    translationBase = "table-select-date-period-filter-by",
    timeLabel = "Time",
    timeLabelTranslationKey = "time-or-date-picker-time-label",
}: TimeOrDatePickerProps<T>) => {
    const { classes, cx } = useStyles()

    const options = React.useMemo(() => {
        if (timePeriodOptions) return timePeriodOptions
        return Object.values(DatePeriod) as T[]
    }, [timePeriodOptions])

    return (
        <Stack gap="0.25rem">
            {isDatePicker ? (
                <Grid container width="auto" gap={1.5} minWidth={20}>
                    <LocalizedStrict id="merchant-accounting-z-reports-table-date-range-filter" attrs={{ label: true }}>
                        <StandardDateRangeFilter
                            inputClasses={classes.inputClasses}
                            dateFilterClasses={classes.dateRangeFilter}
                            value={dateState}
                            onChange={setDateState}
                            label="Date range"
                            disabled={disabled}
                        />
                    </LocalizedStrict>
                </Grid>
            ) : (
                <FormControl fullWidth className={classes.dateFilter} disabled={disabled}>
                    <InputLabel id="time-period-select-label">
                        <LocalizedStrict id={timeLabelTranslationKey}>
                            <>{timeLabel}</>
                        </LocalizedStrict>
                    </InputLabel>
                    <Select<T>
                        labelId={timeLabelTranslationKey}
                        id="time-period-select"
                        value={timePeriod}
                        className={classes.selectClasses}
                        label={timeLabel}
                        onChange={(e) => {
                            handleChangeTimePeriod(e.target.value as T)
                        }}
                    >
                        {options.map((value) => (
                            <MenuItem key={value} value={value}>
                                <LocalizedEnum base={translationBase} enumValue={value}>
                                    <span>{value}</span>
                                </LocalizedEnum>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {!excludeDateTime && (
                <LocalizedStrict
                    id={
                        isDatePicker
                            ? "meeting-service-table-header-period-picker-button"
                            : "meeting-service-table-header-date-range-button"
                    }
                >
                    <Link
                        className={cx(classes.changeTimeButtonSelect, disabled && classes.changeTimeButtonDisabled)}
                        underline="hover"
                        variant="body2"
                        onClick={disabled ? undefined : handleChangeTimeOrDatePicker}
                        aria-disabled={disabled}
                    >
                        Period picker / Date range
                    </Link>
                </LocalizedStrict>
            )}
        </Stack>
    )
}
